// Экспортируем основные типы из общего файла типов
import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import protobuf from 'protobufjs';
import { apiClient as api } from '@/api/client';
import { useDevicePonTasksStore } from "../store/devicePonTasksStore";
import { keepErrorIfSameCause } from "./streamError";
import { applyFDBLiveQuery, type FDBLiveQuery } from './fdbLiveQuery';
import { fdbQueryEnabled, retryFDBQuery } from '../fdbReadLimit';
import { retryVLANQuery, vlanQueryEnabled } from '../vlanReadRefusal';
import { interfacesQueryEnabled, interfacesReadRefusal, retryInterfacesQuery } from '../interfacesReadRefusal';
import type { MonitoringDirectReadCoordinator } from '../monitoringDirectReadCoordinator';

import type {
  Device,
  DeviceFilter,
  DeviceListResponse,
  DeviceInterface,
  FDBEntry,
  ARPEntry,
  DeviceActivityLogItem,
  DeviceActivityLogResponse,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  DeviceNavigationResponse,
  DeviceNavigationItem,
  DeviceNavigationSettings,
  DeviceInventoryIntegration,
  DeviceCommutationListItem,
  CreateInventoryIntegrationPayload,
  DevicePonPortStatsResponse,
  DevicePonPortStat,
  DevicePonPortSummary,
  VendorProfile,
  VendorProfileList,
  VendorProfileTestResult,
  DeviceObserverResponse,
  DeviceObserverConfig,
  AuditEntry,
} from "../types";

export type {
  Device,
  DeviceFilter,
  DeviceListResponse,
  DeviceInterface,
  FDBEntry,
  ARPEntry,
  DeviceActivityLogItem,
  DeviceActivityLogResponse,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  DeviceNavigationResponse,
  DeviceNavigationItem,
  DeviceNavigationSettings,
  DeviceInventoryIntegration,
  DeviceCommutationListItem,
  CreateInventoryIntegrationPayload,
  DevicePonPortStatsResponse,
  DevicePonPortStat,
  DevicePonPortSummary,
  VendorProfile,
  VendorProfileList,
  VendorProfileTestResult,
  DeviceObserverResponse,
  DeviceObserverConfig,
};

import { devicesApi, type ARPOrderField, type ARPReadFacadeV1Item, type ARPReadFacadeV1Meta, type ARPReadFacadeV1Query, type ARPReadFacadeV1Response, type ARPReadSource, type ONUFilter, type ONUListItem, type ONUListResponse, type PollDeviceRequest, type PollDeviceResponse, type StartCableTestRequest, type StartCableTestResponse, type CableTestHistoryItem, type CableTestPortResult, type CableTestPairResult, type CableTestHistoryResponse, type CableTestResultResponse, type PollerTempliteDerived, type PollerTempliteCounters, type PollerTempliteIfListEntry, type PollerTempliteIfTrafficEntry, type PollerTempliteIfErrorsEntry, type PollerPonResult, type PollerVlanEntry, type PollerFdbEntry, type PollerArpEntry, type PollerOnuMeasurement, type PollerPonDownCause, type PollerPonInterface, type PollerPonCacheResponse, type PollerVlanCacheResponse, type PollerTempliteSnapshot, type PollerIfacesCacheResponse, type PollerInterfacePayload, type StartPollerIfacesResponse, type PollerIfacesSyncResponse, type PollerPayload, type ControlInterfaceResponse, type InterfaceAdminAction, type UpdateDeviceObserverPayload } from './devicesApi';
import type { ARPCapabilityV1Response } from './devicesApi';
import { devicesNavigationApi } from './navigationApi';

export { devicesApi, devicesNavigationApi };
export type { ARPOrderField, ARPReadFacadeV1Item, ARPReadFacadeV1Meta, ARPReadFacadeV1Query, ARPReadFacadeV1Response, ARPReadSource, ONUFilter, ONUListItem, ONUListResponse, PollDeviceRequest, PollDeviceResponse, StartCableTestRequest, StartCableTestResponse, CableTestHistoryItem, CableTestPortResult, CableTestPairResult, CableTestHistoryResponse, CableTestResultResponse, PollerTempliteDerived, PollerTempliteCounters, PollerTempliteIfListEntry, PollerTempliteIfTrafficEntry, PollerTempliteIfErrorsEntry, PollerPonResult, PollerVlanEntry, PollerFdbEntry, PollerArpEntry, PollerOnuMeasurement, PollerPonDownCause, PollerPonInterface, PollerPonCacheResponse, PollerVlanCacheResponse, PollerTempliteSnapshot, PollerIfacesCacheResponse, PollerInterfacePayload, StartPollerIfacesResponse, PollerIfacesSyncResponse, PollerPayload, ControlInterfaceResponse, InterfaceAdminAction, UpdateDeviceObserverPayload };
export type { ARPCapabilityV1Response };

// React Query хуки для удобства
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { normalizeOnuListResponse, type NormalizedOnuListResponse, type NormalizedOnuListItem } from './normalizers/onuList';
import { DeviceMonitoringService } from "@shared/api/gen/monitoring/v1/monitoring_pb";
import { transport } from "@shared/api/connect-client";
import { createClient } from "@connectrpc/connect";

const deviceMonitoringClient = createClient(DeviceMonitoringService, transport);

export type { NormalizedOnuListItem, NormalizedOnuListResponse };
export { normalizeOnuListResponse };

const inventoryIntegrationKey = (deviceId: number, port?: number | null) => [
  'device',
  deviceId,
  'inventory-integrations',
  port ?? 'all',
];

const commutationKey = (deviceId: number, port?: number | null) => [
  'device',
  deviceId,
  'commutations',
  port ?? 'all',
];

const ponPortsKey = (deviceId: number) => ['device', deviceId, 'pon', 'ports'];

// Хук для получения одного устройства
export function useDevice(id: number, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      const device = await devicesApi.getById(id);
      return device;
    },
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchInterval !== false,
  });
}

// Хук для получения деталей устройства
export function useDeviceDetails(id: number) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['device', id, 'details'],
    queryFn: () => devicesApi.getDetails(id),
    enabled: !!id,
    placeholderData: () => queryClient.getQueryData<Device>(['device', id]),
  });
}

// Хук для создания устройства
export function useCreateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDeviceRequest) => devicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

// Хук для обновления устройства
export function useUpdateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDeviceRequest }) =>
      devicesApi.update(id, data),
    onSuccess: (updatedDevice, variables) => {
      // Обновляем кеш с новыми данными из ответа API
      // НЕ вызываем invalidateQueries сразу, чтобы не перезаписать только что обновленные данные
      queryClient.setQueryData(['device', variables.id], updatedDevice);
      // Инвалидируем только список устройств (не само устройство), чтобы обновить его в списке
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

// Хук для удаления устройства
export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => devicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

// Хук для получения устройств клиента
export function useCustomerDevices(customerId: number) {
  return useQuery({
    queryKey: ['devices', 'customer', customerId],
    queryFn: () => devicesApi.getByCustomerId(customerId),
    enabled: !!customerId,
  });
}

// Хук для получения интерфейсов устройства
export function useDeviceInterfaces(deviceId: number) {
  return useQuery({
    queryKey: ['device', deviceId, 'interfaces'],
    queryFn: () => devicesApi.getInterfaces(deviceId),
    enabled: !!deviceId,
  });
}

export function useDeviceInterfacesWithMeta(deviceId: number, options?: { enabled?: boolean; staleTime?: number; refetchInterval?: number | false; refetchIntervalInBackground?: boolean }) {
  return useQuery({
    queryKey: ['device', deviceId, 'interfaces', 'meta'],
    queryFn: () => devicesApi.getInterfacesWithMeta(deviceId),
    enabled: options?.enabled !== false && !!deviceId,
    staleTime: options?.staleTime,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchIntervalInBackground,
  });
}

// Хук для получения интерфейсов через Direct SNMP (прямой запрос к устройству)
export interface DeviceInterfacesTrafficResponse {
  traffic: Array<{
    if_index: number;
    in_octets: number;
    out_octets: number;
    rx: number; // биты/сек (вычисляется на frontend)
    tx: number; // биты/сек (вычисляется на frontend)
  }>;
  source: string;
  updated_at: string;
}

/**
 * Получить трафик интерфейсов устройства через Direct SNMP
 */
export async function getDeviceInterfacesTraffic(
  deviceId: number,
  ifIndexes?: number[],
  signal?: AbortSignal,
): Promise<DeviceInterfacesTrafficResponse> {
  return devicesApi.getInterfacesTraffic(deviceId, { ifIndexes }, signal);
}

/**
 * Hook для получения трафика интерфейсов с автоматическим обновлением
 */
export function useDeviceInterfacesTraffic(
  deviceId: number,
  ifIndexes?: number[],
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled !== false && !!deviceId;
  const refetchInterval = options?.refetchInterval ?? 3000; // ✅ 3 секунды для частого обновления трафика

  // ✅ ОПТИМИЗАЦИЯ: Проверяем видимость страницы для остановки запросов когда вкладка неактивна
  const [isPageVisible, setIsPageVisible] = useState(() => {
    if (typeof document !== 'undefined') {
      return !document.hidden;
    }
    return true;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const query = useQuery({
    queryKey: ['device', deviceId, 'interfaces', 'traffic', Array.isArray(ifIndexes) ? ifIndexes.join(',') : undefined],
    queryFn: ({ signal }) => getDeviceInterfacesTraffic(deviceId, Array.isArray(ifIndexes) ? ifIndexes : undefined, signal),
    enabled: (query) => interfacesQueryEnabled(enabled && isPageVisible, query.state.error),
    // ✅ ИНТЕРВАЛ ОПРОСА: 3 секунды для частого обновления трафика
    // Кеш SNMP на backend (5 секунд) защищает оборудование от множественных запросов
    refetchInterval: (query) => interfacesQueryEnabled(enabled && isPageVisible, query.state.error) ? refetchInterval : false,
    refetchIntervalInBackground: false, // ✅ НЕ опрашиваем в фоне (экономия ресурсов)
    staleTime: 0, // ✅ КРИТИЧНО: Трафик должен обновляться всегда (не использовать кеш)
    gcTime: 5 * 60 * 1000, // 5 минут - храним в памяти для быстрого доступа
    refetchOnMount: (query) => interfacesReadRefusal(query.state.error) === null,
    refetchOnWindowFocus: false, // Не обновляем при фокусе окна (экономия ресурсов)
    retry: retryInterfacesQuery,
  });

  return query;
}

// ✅ НОВОЕ (2026-01-12): EventSource для streaming данных с защитой от ошибок
// ✅ УЛУЧШЕНИЕ (2026-01-22): Поддержка всех стадий стриминга и Live-обновлений
// ✅ НОВОЕ (2026-01-12): EventSource для streaming данных с защитой от ошибок
// ✅ УЛУЧШЕНИЕ (2026-01-22): Поддержка всех стадий стриминга и Live-обновлений
import { useAuthStore } from "@features/auth/store/authStore";

// ... (existing imports) ...

import { isTokenExpired } from '@shared/utils/jwt';
import { toArray } from '@shared/lib/toArray';
import { shouldRetryViaRest } from './restFallback';

// ✅ РЕФАКТОРИНГ (2026-01-22): Использование useRef для предотвращения stale closures и авто-реконнект
// ✅ АРХИТЕКТУРА (2026-01-19): Protobuf определение для декодирования SSE стрима
const monitoringProto = `
syntax = "proto3";
package monitoring.v1;

message Interface {
  int32 index = 1;
  string name = 2;
  string description = 3;
  int32 admin_status = 4;
  int32 oper_status = 5;
  int64 speed = 6;
  int32 mtu = 7;
  int32 type = 8;
  uint64 in_octets = 9;
  uint64 out_octets = 10;
  uint64 in_errors = 11;
  uint64 out_errors = 12;
  uint64 in_discards = 13;
  uint64 out_discards = 14;
  uint64 in_ucast_pkts = 15;
  uint64 out_ucast_pkts = 16;
  string phys_address = 17;
  int64 last_change = 18;
}

message InterfaceStreamUpdate {
  repeated Interface interfaces = 1;
  string stage = 2;
  bool is_delta = 3;
  string source = 4;
  string poll_type = 5;
  int64 timestamp = 6;
  string error = 7;
  string snmp_method = 8;
  string interface_errors_status = 9;
  string interface_discards_status = 10;
}
`;

const root = protobuf.parse(monitoringProto).root;
const UpdateMessage = root.lookupType("monitoring.v1.InterfaceStreamUpdate");

/**
 * @deprecated Use useDeviceInterfacesStreamV2 from useMonitoringStream.ts instead (gRPC-Web)
 * Хук для получения интерфейсов устройства через SSE (Server-Sent Events)
 */
export const useDeviceInterfacesStream = (deviceId: number, enabled: boolean = true) => {
  const { t } = useTranslation(['auth']);
  const [interfaces, setInterfaces] = useState<DeviceInterface[]>([]);
  const [stage, setStage] = useState<"initial" | "essential" | "static" | "counters" | "complete" | "live" | "error" | "idle">("idle");
  const [source, setSource] = useState<string>("");
  const [pollType, setPollType] = useState<string>("");
  const [snmpMethod, setSnmpMethod] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const [refetchKey, setRefetchKey] = useState(0);

  const refetch = () => setRefetchKey(prev => prev + 1);

  // ✅ БЕЗОПАСНОСТЬ: Получаем токен для EventSource (так как он не поддерживает кастомные заголовки)
  // Токен отдаётся стриму ТОЛЬКО при аутентифицированной сессии: сырые ключи
  // localStorage сюда не читаются, чтобы переживший выход токен не открыл SSE.
  const storeToken = useAuthStore(state => state.accessToken);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const devToken = import.meta.env.DEV ? (import.meta.env.VITE_DEV_AUTH_TOKEN || 'dev-local-token') : undefined;
  const token = isAuthenticated ? (storeToken || devToken) : null;

  // ✅ Архитектурное улучшение: useRef для отслеживания стадии без перезапуска эффекта
  const stageRef = useRef<typeof stage>("idle");
  const retryCountRef = useRef(0);
  const pendingUpdatesRef = useRef<Map<number, any>>(new Map());
  const maxRetries = 5;

  const cacheKey = `workernet_device_ifaces_${deviceId}`;

  // Синхронизируем стейт и реф
  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);

  // 1. Инициализация из LocalStorage для мгновенного отображения (Predictive UI)
  useEffect(() => {
    if (enabled && deviceId) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          // Показываем кеш только если он не совсем древний (например, < 1 часа)
          if (Date.now() - parsed.timestamp < 3600 * 1000) {
            setInterfaces(Array.isArray(parsed.interfaces) ? parsed.interfaces.map((iface: DeviceInterface) => ({ ...iface, errors_measured: false, discards_measured: false })) : []);
            setLastUpdate(Math.floor(parsed.timestamp / 1000));
            setStage("initial"); // Сразу переходим в стадию initial (оптимистично)
            setSource("local_cache");
          }
        } catch (e) {
          console.warn("Failed to parse device local cache", e);
        }
      }
    }
  }, [deviceId, enabled, cacheKey]);

  useEffect(() => {
    if (!enabled || !deviceId) {
      setStage("idle");
      return;
    }

    // ✅ БЕЗОПАСНОСТЬ: Если токен истек или отсутствует, не пытаемся подключиться
    if (!token || isTokenExpired(token)) {
      console.warn(`[SSE] Connection skipped: ${!token ? 'token missing' : 'token expired'}`);
      setStage("error");
      setError(keepErrorIfSameCause(t('auth:errors.token_expired', 'Сессия истекла. Пожалуйста, войдите снова.')));
      return;
    }

    let timeoutId: any = null;
    let eventSource: EventSource | null = null;
    let reconnectTimeoutId: any = null;

    // ✅ UX (2026-01-19): Троттлинг обновлений для снижения нагрузки на рендеринг
    // Группируем дельта-обновления и применяем их раз в 500мс
    const applyPendingUpdates = () => {
      if (pendingUpdatesRef.current.size === 0) return;

      setInterfaces((prev) => {
        const nextInterfaces = [...prev];
        let hasChanges = false;

        pendingUpdatesRef.current.forEach((update, if_index) => {
          const index = nextInterfaces.findIndex((iface) => iface.if_index === if_index);
          if (index !== -1) {
            nextInterfaces[index] = { ...nextInterfaces[index], ...update };
            hasChanges = true;
          } else {
            nextInterfaces.push(update as DeviceInterface);
            hasChanges = true;
          }
        });

        pendingUpdatesRef.current.clear();

        if (hasChanges && stageRef.current !== "error") {
          localStorage.setItem(cacheKey, JSON.stringify({
            interfaces: nextInterfaces,
            timestamp: Date.now()
          }));
        }

        return nextInterfaces;
      });
    };

    const throttleId = setInterval(applyPendingUpdates, 500);

    const connect = () => {
      if (eventSource) {
        eventSource.close();
      }

      // Если нет данных из LocalStorage, ставим initial
      if (interfaces.length === 0 && stageRef.current === "idle") {
        setStage("initial");
      }
      setError(null);

      // ✅ КРИТИЧНО: Таймаут для начальной загрузки (теперь 95 секунд, больше бэкенда)
      timeoutId = setTimeout(() => {
        // Используем stageRef.current вместо stage для актуального значения
        if (stageRef.current !== "complete" && stageRef.current !== "live") {
          console.error(`[SSE] Stream timeout after 95 seconds for device ${deviceId}`);
          eventSource?.close();
          setError(keepErrorIfSameCause("Stream timeout after 95 seconds"));
          setStage("error");
        }
      }, 95 * 1000);

      // ✅ КРИТИЧНО: Передаем токен в URL, так как EventSource не поддерживает заголовки
      // ✅ АРХИТЕКТУРА (2026-01-19): Запрашиваем формат protobuf для экономии трафика
      const hasToken = token && token.length > 0;
      const url = `/api/v1/devices/${deviceId}/interfaces/stream?format=protobuf${hasToken ? `&token=${encodeURIComponent(token!)}` : ''}`;
      if (!hasToken) {
        console.error(`[SSE] ❌ ERROR: NO AUTH TOKEN FOUND! CONNECTION WILL FAIL.`);
      }
      eventSource = new EventSource(url);

      eventSource.onopen = () => {
        retryCountRef.current = 0; // Сбрасываем при открытии
      };

      eventSource.onmessage = (event) => {
        try {
          let data: any;

          // ✅ АРХИТЕКТУРА (2026-01-19): Декодирование Protobuf из Base64
          if (typeof event.data === 'string' && !event.data.startsWith('{')) {
            const buffer = Uint8Array.from(atob(event.data), (c) => c.charCodeAt(0));
            const decoded = UpdateMessage.decode(buffer);
            data = UpdateMessage.toObject(decoded, {
              longs: Number,
              enums: String,
              bytes: String,
            });

            // Мапим поля прото на поля фронтенда (if_index)
            if (data.interfaces) {
              data.interfaces = data.interfaces.map((iface: any) => ({
                ...iface,
                if_index: iface.index, // Mapping proto 'index' -> domain 'if_index'
              }));
            }
          } else {
            data = JSON.parse(event.data);
          }

          // Сбрасываем счетчик попыток при успешном сообщении
          retryCountRef.current = 0;

          if (data.error) {
            console.error(`[SSE] Application error:`, data.error);
            setError(keepErrorIfSameCause(data.error.message || "Stream error"));
            setStage("error");
            eventSource?.close();
            if (timeoutId) clearTimeout(timeoutId);
            return;
          }

          if (data.interfaces) {
            const errorsMeasured = data.interface_errors_status === 'complete' && data.source === 'snmp';
            data.interfaces = data.interfaces.map((iface: any) => ({
              ...iface,
              in_errors: errorsMeasured ? Number(iface.in_errors ?? 0) : 0,
              out_errors: errorsMeasured ? Number(iface.out_errors ?? 0) : 0,
              in_discards: errorsMeasured ? Number(iface.in_discards ?? 0) : 0,
              out_discards: errorsMeasured ? Number(iface.out_discards ?? 0) : 0,
              errors_measured: errorsMeasured,
              discards_measured: false,
            }));
            if (data.is_delta) {
              // ✅ UX (2026-01-19): Буферизируем дельта-обновления для троттлинга
              data.interfaces.forEach((update: any) => {
                const existing = pendingUpdatesRef.current.get(update.if_index) || {};
                pendingUpdatesRef.current.set(update.if_index, { ...existing, ...update });
              });
            } else {
              // Полное обновление применяем сразу
              pendingUpdatesRef.current.clear();
              setInterfaces(data.interfaces);

              if (data.stage !== "error") {
                localStorage.setItem(cacheKey, JSON.stringify({
                  interfaces: data.interfaces,
                  timestamp: Date.now()
                }));
              }
            }
          }

          if (data.stage) {
            setStage(data.stage);
          }

          if (data.source) {
            setSource(data.source);
          }

          if (data.poll_type) {
            setPollType(data.poll_type);
          }

          if (data.snmp_method) {
            setSnmpMethod(data.snmp_method);
          }

          setLastUpdate(data.timestamp || Date.now());

          if (data.stage === "complete" || data.stage === "live") {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
          }
        } catch (err) {
          console.error(`[SSE] Failed to parse message:`, err);
          setError(err instanceof Error ? err : new Error("Parse error"));
          setStage("error");
          eventSource?.close();
          if (timeoutId) clearTimeout(timeoutId);
        }
      };

      eventSource.onerror = (err) => {
        // Используем stageRef.current для актуального значения
        if (stageRef.current !== "complete" && stageRef.current !== "live") {
          console.warn(`[SSE] Connection error for device ${deviceId}, retry ${retryCountRef.current + 1}/10`);

          eventSource?.close();
          if (timeoutId) clearTimeout(timeoutId);

          if (retryCountRef.current < 10) { // Увеличили до 10 попыток
            const delay = Math.min(1000 * Math.pow(1.5, retryCountRef.current), 30000); // Экспоненциальный бэкофф
            retryCountRef.current++;

            reconnectTimeoutId = setTimeout(() => {
              connect();
            }, delay);
          } else {
            // Пытаемся выяснить причину через fetch
            fetch(url).then(async (res) => {
              if (!res.ok) {
                const text = await res.text();
                setError(keepErrorIfSameCause(`Connection failed: ${res.status} ${res.statusText}. ${text}`));
              } else {
                setError(keepErrorIfSameCause("EventSource connection error after 10 retries"));
              }
            }).catch(() => {
              setError(keepErrorIfSameCause("EventSource connection error (backend unreachable)"));
            });
            setStage("error");
          }
        } else {
          // ✅ UX (2026-01-22): Автоматический реконнект для стадии Live
          // Если соединение закрыто по таймауту бэкенда (10 мин), но вкладка активна - переподключаемся
          eventSource?.close();
          if (timeoutId) clearTimeout(timeoutId);

          // Переподключаемся через небольшую паузу (2 сек), чтобы не спамить
          if (document.visibilityState === 'visible') {
            reconnectTimeoutId = setTimeout(() => {
              connect();
            }, 2000);
          }
        }
      };
    };

    connect();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      if (eventSource) eventSource.close();
      clearInterval(throttleId);
    };
  }, [deviceId, enabled, cacheKey, token, refetchKey]); // ✅ Удален interfaces.length для предотвращения циклического перезапуска

  return {
    interfaces,
    stage,
    source,
    pollType,
    snmpMethod,
    error,
    lastUpdate,
    refetch,
    isLoading: stage !== "complete" && stage !== "live" && stage !== "error" && stage !== "idle" && interfaces.length === 0,
    isLive: stage === "live",
    isProtobuf: true, // ✅ UX: Protobuf всегда активен для SSE стрима
  };
};

export function useDeviceInterfacesDirect(
  deviceId: number,
  options?: {
    ifIndexes?: number[];
    includeErrors?: boolean;
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchIntervalInBackground?: boolean;
    basicOnly?: boolean; // ✅ UX УЛУЧШЕНИЕ (Phase 0): true = только базовые OID (быстрее)
    oidLevel?: number; // ✅ ФАЗА 2: 1=essential (3 OID), 2=important (7 OID), 3=extended (12 OID)
    physicalOnly?: boolean; // ✅ ИСПРАВЛЕНИЕ (2026-01-06): true = только физические интерфейсы (по умолчанию), false = все интерфейсы
  },
) {
  // ✅ МИРОВЫЕ ПРАКТИКИ: динамические таймауты по уровню OID
  // ✅ ИСПРАВЛЕНИЕ 2026-01-07: Увеличены таймауты согласно DIRECT_SNMP_ТАЙМАУТЫ_ИСПРАВЛЕНИЕ.md
  // L1 (essential): 25s (увеличено с 15s для SNMP Walk на больших OLT)
  // L2 (important): 40s (больше backend 35s + запас)
  // L3 (extended): 100s (больше backend 85s + запас)
  const getTimeoutMs = (level?: number): number => {
    if (level === 1) return 25000;  // ✅ УВЕЛИЧЕНО: 25s для L1 (было 15s)
    if (level === 2) return 40000;  // 40s для L2
    if (level === 3) return 100000; // 100s для L3
    return 15000; // дефолт L1
  };

  return useQuery({
    queryKey: [
      'device',
      deviceId,
      'interfaces',
      'direct',
      options?.ifIndexes?.join(','),
      options?.oidLevel ?? 'full',
      options?.basicOnly ? 'basic' : 'full',
      options?.physicalOnly === false ? 'all' : 'physical',
    ],
    queryFn: async (queryInfo) => {
      const signal = queryInfo.signal;
      const startTime = Date.now();

      try {
        const result = await devicesApi.getInterfacesDirect(
          deviceId,
          {
            ifIndexes: options?.ifIndexes,
            includeErrors: options?.includeErrors ?? true,
            basicOnly: options?.basicOnly ?? false,
            oidLevel: options?.oidLevel,
            physicalOnly: options?.physicalOnly ?? true, // ✅ ИСПРАВЛЕНИЕ (2026-01-06): По умолчанию только физические интерфейсы
          },
          signal, // ✅ Pass signal to API for unmount cancellation
        );

        // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Логируем метаданные кеша для оптимизации
        const cacheMetadata = (result as any)?._cacheMetadata;
        if (cacheMetadata) {
          const duration = Date.now() - startTime;
        }

        return result;
      } catch (error) {
        // ✅ Проверяем тип ошибки
        if (error instanceof Error) {
          // AbortError - запрос отменен при unmount/tab change
          if (error.name === 'AbortError' || error.message === 'Request cancelled') {
            throw error;
          }
          // Timeout - axios timeout или другая сетевая ошибка
          if ((error as any).code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error('Request timeout - device not responding');
          }
        }
        throw error;
      }
    },
    enabled: (query) => interfacesQueryEnabled((options?.enabled !== false) && !!deviceId, query.state.error),
    refetchInterval: (query) => interfacesQueryEnabled((options?.enabled !== false) && !!deviceId, query.state.error)
      ? options?.refetchInterval ?? false
      : false,
    refetchIntervalInBackground: options?.refetchIntervalInBackground ?? false,
    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Адаптивный staleTime на основе метаданных кеша
    // Если данные из кеша - используем больший staleTime для мгновенного отображения
    // Если данные из Direct SNMP - используем меньший staleTime для актуальности
    staleTime: (query) => {
      const data = query.state.data as any;
      const cacheMetadata = data?._cacheMetadata;
      if (cacheMetadata?.isFromCache) {
        // Данные из кеша - считаем их свежими дольше (30 секунд)
        return cacheMetadata.isCacheFresh ? 30000 : 5000; // Свежий кеш: 30s, устаревший: 5s
      }
      // Данные из Direct SNMP - считаем их свежими 5 секунд
      return 5000;
    },
    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Не refetch если данные из свежего кеша
    refetchOnMount: (query) => {
      if (interfacesReadRefusal(query.state.error)) return false;
      const data = query.state.data as any;
      const cacheMetadata = data?._cacheMetadata;
      // Если данные из свежего кеша - не refetch при mount
      if (cacheMetadata?.isFromCache && cacheMetadata?.isCacheFresh) {
        return false;
      }
      return true;
    },
    refetchOnWindowFocus: false,
    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Auto-retry с экспоненциальной задержкой для 503
    // Retry только для 503 (Service Unavailable) и network errors, не для timeout/abort
    retry: (failureCount, error) => {
      if (interfacesReadRefusal(error)) return false;
      // Не retry для AbortError (отмена запроса)
      if (error instanceof Error && (error.name === 'AbortError' || error.message === 'Request cancelled')) {
        return false;
      }
      // Не retry для timeout
      if (error instanceof Error && ((error as any).code === 'ECONNABORTED' || error.message.includes('timeout'))) {
        return false;
      }
      // Retry для 503 (Service Unavailable) и network errors
      if ((error as any)?.response?.status === 503 || (error as any)?.code === 'ECONNREFUSED' || (error as any)?.code === 'ENOTFOUND') {
        return failureCount < 3; // Максимум 3 попытки
      }
      return false;
    },
    retryDelay: (attemptIndex) => {
      // Экспоненциальная задержка: 1s, 2s, 4s (максимум 30s)
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    gcTime: 5 * 60 * 1000,
    networkMode: 'always', // ✅ Гарантировать что запрос идет даже если есть ошибка
  });
}

export function useDeviceInventoryIntegrations(
  deviceId: number,
  options?: { port?: number; enabled?: boolean },
) {
  const port = options?.port;
  const enabled = options?.enabled ?? !!deviceId;
  return useQuery<DeviceInventoryIntegration[]>({
    queryKey: inventoryIntegrationKey(deviceId, port),
    queryFn: () => devicesApi.getInventoryIntegrations(deviceId, port),
    enabled,
  });
}

export function useCreateInventoryIntegration(deviceId: number) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, CreateInventoryIntegrationPayload>({
    mutationFn: (payload) => devicesApi.createInventoryIntegration(deviceId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryIntegrationKey(deviceId, null) });
      queryClient.invalidateQueries({ queryKey: inventoryIntegrationKey(deviceId, variables.port ?? null) });
    },
  });
}

export function useDeleteInventoryIntegration(deviceId: number) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { inventoryId: number; port?: number }>({
    mutationFn: ({ inventoryId }) => devicesApi.deleteInventoryIntegration(deviceId, inventoryId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryIntegrationKey(deviceId, null) });
      queryClient.invalidateQueries({ queryKey: inventoryIntegrationKey(deviceId, variables.port ?? null) });
    },
  });
}

export function useDeviceCommutations(deviceId: number, options?: { port?: number; enabled?: boolean }) {
  const port = options?.port;
  const enabled = options?.enabled ?? !!deviceId;
  return useQuery<DeviceCommutationListItem[]>({
    queryKey: commutationKey(deviceId, port),
    queryFn: () => devicesApi.getDeviceCommutations(deviceId, port),
    enabled,
  });
}

export function useDevicePonPortStats(deviceId: number, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? !!deviceId;
  return useQuery<DevicePonPortStatsResponse>({
    queryKey: ponPortsKey(deviceId),
    queryFn: () => devicesApi.getDevicePonPortStats(deviceId),
    enabled,
    staleTime: 30_000,
  });
}

export function useDevicePonMetrics(deviceId: number, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? !!deviceId;
  return useQuery({
    queryKey: ['device', deviceId, 'pon', 'metrics'],
    queryFn: () => devicesApi.getDevicePonMetrics(deviceId),
    enabled,
    staleTime: 30_000,
    // ✅ ОПТИМИЗАЦИЯ: Не рефетчить при фокусе окна (данные обновляются через WebSocket)
    refetchOnWindowFocus: false,
  });
}

// Хук для получения FDB таблицы

export function useDeviceFDB(deviceId: number, options?: { enabled?: boolean; page?: number; limit?: number; filters?: FDBLiveQuery; coordinator?: MonitoringDirectReadCoordinator }) {
  const enabled = options?.enabled ?? false; // По умолчанию выключено, если не указано явно
  return useQuery({
    queryKey: ['device', deviceId, 'fdb', options?.page, options?.limit, options?.filters],
    queryFn: async ({ signal }) => {
      const request = async (requestSignal: AbortSignal) => {
        try {
          const response = await deviceMonitoringClient.getDeviceFDB({
            deviceId: BigInt(deviceId),
            page: options?.page ?? 1,
            limit: options?.limit ?? 100,
            macFilter: options?.filters?.mac ?? "",
            vlanFilter: options?.filters?.vlan_id ?? 0,
            portFilter: options?.filters?.port == null ? '' : String(options.filters.port),
            typeFilter: options?.filters?.type ?? '',
            searchFilter: options?.filters?.search ?? '',
            sortBy: options?.filters?.sort_by ?? '',
            sortDesc: options?.filters?.sort_desc ?? false,
            problemFilter: options?.filters?.problem === 'all' ? '' : options?.filters?.problem ?? '',
          }, { signal: requestSignal });
          return {
            items: response.entries.map((entry) => ({
              mac_address: entry.macAddress,
              vlan_id: entry.vlanId,
              port: entry.port,
              port_name: entry.portName,
              type: entry.type === 1 ? 'static' : entry.type === 2 ? 'dynamic' : 'unknown',
              date_first: entry.dateFirst ? new Date(Number(entry.dateFirst) * 1000).toISOString() : undefined,
              date_last: entry.dateLast ? new Date(Number(entry.dateLast) * 1000).toISOString() : undefined,
            })),
            pagination: {
              page: options?.page ?? 1,
              limit: options?.limit ?? 100,
              total: response.totalCount,
              total_pages: response.totalPages,
            },
            duplicateCount: response.duplicateCount,
            source: 'gRPC', // ✅ UX: Индикатор источника данных
          };
        } catch (error) {
          if (!shouldRetryViaRest(error, requestSignal)) {
            // Сервер ответил отказом — он уже сходил к устройству. Второй заход тем же
            // путём только отодвинет причину от оператора.
            throw error;
          }
          console.error('Failed to fetch FDB via gRPC transport, falling back to REST:', error);
          const entries = await devicesApi.getFDB(deviceId, requestSignal);
          const selected = applyFDBLiveQuery(entries, options?.filters ?? {});
          const page = options?.page ?? 1;
          const limit = options?.limit ?? 100;
          const start = (page - 1) * limit;
          return {
            items: selected.items.slice(start, start + limit).map((entry) => ({
              ...entry,
              // REST Direct-FDB also synthesizes poll-time dates. They are not
              // first/last-seen history and must match the Connect representation.
              date_first: '', date_last: '',
            })),
            pagination: {
              page,
              limit,
              total: selected.items.length,
              total_pages: Math.ceil(selected.items.length / limit),
            },
            duplicateCount: selected.duplicateCount,
            source: 'REST', // ✅ UX: Индикатор источника данных
          };
        }
      };
      return options?.coordinator ? options.coordinator.run('fdb', signal, request) : request(signal);
    },
    enabled: (query) => fdbQueryEnabled(enabled, query.state.error, query.state.errorUpdatedAt),
    retry: retryFDBQuery,
    placeholderData: keepPreviousData,
  });
}

const MAX_INT32 = 2_147_483_647;

interface DeviceARPReadFacadeV1Options {
  enabled?: boolean;
  coordinator?: MonitoringDirectReadCoordinator;
  page?: number;
  limit?: number;
  filters?: ARPReadFacadeV1Query['filters'];
  sort_by?: ARPOrderField;
  sort_desc?: boolean;
}

function arpReadFacadeV1Page(value: number | undefined): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= MAX_INT32
    ? value
    : 1;
}

function arpReadFacadeV1Limit(value: number | undefined): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 200
    ? value
    : 50;
}

export function useDeviceARPCapabilityV1(deviceId: number, options?: { enabled?: boolean }) {
  return useQuery<ARPCapabilityV1Response>({
    queryKey: ['device', deviceId, 'arp-capability-v1'],
    queryFn: ({ signal }) => devicesApi.getARPCapabilityV1(deviceId, signal),
    enabled: options?.enabled ?? false,
    retry: false,
  });
}

export function useDeviceARPReadFacadeV1(
  deviceId: number,
  options?: DeviceARPReadFacadeV1Options,
) {
  const page = arpReadFacadeV1Page(options?.page);
  const limit = arpReadFacadeV1Limit(options?.limit);
  return useQuery({
    queryKey: [
      'device',
      deviceId,
      'arp-read-facade-v1',
      page,
      limit,
      options?.filters,
      options?.sort_by,
      options?.sort_desc,
    ],
    queryFn: ({ signal }) => {
      const request = (requestSignal: AbortSignal) => devicesApi.getARPReadFacadeV1(deviceId, page, limit, {
        filters: options?.filters,
        sort_by: options?.sort_by,
        sort_desc: options?.sort_desc,
        signal: requestSignal,
      });
      return options?.coordinator ? options.coordinator.run('arp', signal, request) : request(signal);
    },
    enabled: options?.enabled ?? false,
    retry: false,
  });
}

// Пагинированный хук FDB (перевод на gRPC)
export function useDeviceFDBPaged(
  deviceId: number,
  page: number,
  limit: number,
  options?: { enabled?: boolean; filters?: FDBLiveQuery; sort_by?: FDBLiveQuery['sort_by']; sort_desc?: boolean; coordinator?: MonitoringDirectReadCoordinator },
) {
  return useDeviceFDB(deviceId, {
    enabled: options?.enabled,
    page,
    limit,
    filters: { ...options?.filters, sort_by: options?.sort_by, sort_desc: options?.sort_desc },
    coordinator: options?.coordinator,
  });
}

export function useDeviceVLANPaged(
  deviceId: number,
  page: number,
  limit: number,
  options?: { enabled?: boolean; filters?: { vlan_id?: number; name?: string; status?: string }; sort_by?: string; sort_desc?: boolean; coordinator?: MonitoringDirectReadCoordinator },
) {
  const enabled = options?.enabled ?? false;
  return useQuery({
    queryKey: ['device', deviceId, 'vlan', 'paged', page, limit, options?.filters ?? null, options?.sort_by ?? null, options?.sort_desc ?? null],
    queryFn: ({ signal }) => {
      const request = (requestSignal: AbortSignal) => devicesApi.getVlanPaged(deviceId, page, limit, { filters: options?.filters, sort_by: options?.sort_by, sort_desc: options?.sort_desc, signal: requestSignal });
      return options?.coordinator ? options.coordinator.run('vlan', signal, request) : request(signal);
    },
    enabled: (query) => vlanQueryEnabled(enabled, query.state.error),
    retry: retryVLANQuery,
  });
}

// Хук для поиска по MAC
export function useFindMAC(macAddress: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['devices', 'mac', macAddress],
    queryFn: () => devicesApi.findMAC(macAddress),
    enabled: options?.enabled ?? (macAddress.length > 0),
  });
}

// ✅ РЕФАКТОРИНГ (2026-01-XX): Удален usePollerInterfaces - используйте useDeviceInterfacesDirect

export function usePollerPon(deviceId: number, task: string, enabled: boolean) {
  return useQuery<PollerPonCacheResponse>({
    queryKey: ['devices', deviceId, 'poller', 'pon', task],
    queryFn: () => devicesApi.getPollerPon(deviceId, task),
    enabled: enabled && !!deviceId && !!task,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ✅ РЕФАКТОРИНГ (2026-01-XX): Удален usePollerVlan - используйте useDeviceVLANPaged

export function useDeviceNavigation(options?: { enabled?: boolean }) {
  return useQuery<DeviceNavigationResponse>({
    queryKey: ['navigation', 'devices'],
    queryFn: () => devicesNavigationApi.get(),
    enabled: options?.enabled ?? true,
    staleTime: 0, // Отключаем кэш для разработки - меню должно обновляться сразу
  });
}

// ✅ ВОССТАНОВЛЕНО: Хуки для обратной совместимости со старым кодом
// Синхронный опрос Poller (IfList)
export function usePollerInterfaces(deviceId: number, enabled: boolean = true) {
  return useQuery<PollerIfacesCacheResponse>({
    queryKey: ['devices', deviceId, 'poller', 'ifaces'],
    queryFn: async () => {
      const response = await api.get<PollerIfacesCacheResponse>(`/devices/${deviceId}/poller/ifaces`);
      return response.data;
    },
    enabled: enabled && !!deviceId,
    staleTime: 30 * 1000,
    refetchInterval: false,
  });
}

export function usePollerInterfacesSync() {
  const queryClient = useQueryClient();
  
  return useMutation<PollerIfacesSyncResponse, unknown, { deviceId: number; interfaces?: number[] }>({
    mutationFn: async ({ deviceId, interfaces }) => {
      const response = await api.post<PollerIfacesSyncResponse>(
        `/devices/${deviceId}/poller/ifaces/sync`,
        { interfaces }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['devices', variables.deviceId, 'poller', 'ifaces'] });
      queryClient.invalidateQueries({ queryKey: ['device', variables.deviceId, 'interfaces'] });
    },
  });
}

export function useStartPollerIfaces(deviceId: number) {
  const queryClient = useQueryClient();
  
  return useMutation<StartPollerIfacesResponse, unknown, { profile?: string }>({
    mutationFn: async (payload) => {
      const response = await api.post<StartPollerIfacesResponse>(
        `/devices/${deviceId}/poller/ifaces`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId, 'poller', 'ifaces'] });
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'interfaces'] });
    },
  });
}

// Хук для получения настроек планировщика Poller
export function useDeviceObserver(deviceId: number, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? !!deviceId;

  return useQuery<DeviceObserverResponse>({
    queryKey: ['device', deviceId, 'observer'],
    queryFn: () => devicesApi.getObserver(deviceId),
    enabled,
  });
}

// Хук для обновления настроек планировщика Poller
export function useUpdateDeviceObserver(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation<DeviceObserverConfig, unknown, UpdateDeviceObserverPayload>({
    mutationFn: (payload) => devicesApi.updateObserver(deviceId, payload),
    onSuccess: (observer) => {
      queryClient.setQueryData<DeviceObserverResponse | undefined>(
        ['device', deviceId, 'observer'],
        (prev) => ({
          observer,
          profiles: prev?.profiles ?? [],
        }),
      );
    },
  });
}

export function useControlInterface(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation<ControlInterfaceResponse, unknown, { action: InterfaceAdminAction; interfaces: number[] }>({
    mutationFn: (payload) => devicesApi.controlInterface(deviceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'interfaces'] });
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId, 'poller', 'ifaces'] });
    },
  });
}

/**
 * Hook для ручного сброса SNMP кеша интерфейсов устройства
 */
export function useInvalidateDeviceInterfacesCache(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => devicesApi.invalidateInterfacesCache(deviceId),
    onSuccess: () => {
      // Инвалидируем все связанные с интерфейсами запросы
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'interfaces'] });
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'interfaces', 'direct'] });
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'interfaces', 'meta'] });

      // Сбрасываем LocalStorage кеш для этого устройства
      const cacheKey = `workernet_device_ifaces_${deviceId}`;
      localStorage.removeItem(cacheKey);
    },
  });
}

function toNumber(value: number | string | null | undefined): number | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function extractPollerInterfaces(result?: { interfaces?: PollerInterfacePayload[] }) {
  if (!result?.interfaces) {
    return [];
  }

  return result.interfaces.map((item) => {
    const normalizedIndex =
      typeof item.if_index === 'number'
        ? item.if_index
        : typeof item.index === 'number'
          ? item.index
          : Number(item.if_index ?? item.index ?? 0);

    // ✅ ОТЛАДКА: Логируем большие числа в if_index из исходных данных Poller
    if (process.env.NODE_ENV === 'development' && normalizedIndex > 1000000) {
      console.warn(`[extractPollerInterfaces] Большое число в if_index из Poller!`, {
        original_if_index: item.if_index,
        original_index: item.index,
        normalizedIndex,
        name: item.name,
        description: item.description,
        speed: item.speed,
        in_octets: item.in_octets,
        out_octets: item.out_octets,
        allKeys: Object.keys(item),
        rawItem: item, // Полный объект для анализа
      });
    }

    const speed = toNumber(item.speed);

    return {
      ...item,
      index: normalizedIndex,
      if_index: normalizedIndex,
      speed,
      mtu: toNumber(item.mtu),
      type: toNumber(item.type),
      in_octets: toNumber(item.in_octets),
      out_octets: toNumber(item.out_octets),
      in_errors: toNumber(item.in_errors),
      out_errors: toNumber(item.out_errors),
      in_discards: toNumber(item.in_discards),
      out_discards: toNumber(item.out_discards),
      in_ucast_pkts: toNumber(item.in_ucast_pkts),
      out_ucast_pkts: toNumber(item.out_ucast_pkts),
      last_change: toNumber(item.last_change),
    };
  });
}

export function extractPollerVlans(result?: { vlans?: PollerVlanEntry[] }) {
  if (!result?.vlans) {
    return [];
  }
  return result.vlans;
}

export function extractPollerFdb(result?: { fdb?: PollerFdbEntry[] }) {
  if (!result?.fdb) {
    return [];
  }
  return result.fdb;
}

export function extractPollerArp(result?: { arp?: PollerArpEntry[] }) {
  if (!result?.arp) {
    return [];
  }
  return result.arp;
}

export function extractPollerTemplite(result?: { templite?: PollerTempliteSnapshot }) {
  if (!result?.templite) {
    return undefined;
  }
  return result.templite;
}

// Хук для опроса устройства
export function usePollDevice(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation<PollDeviceResponse, unknown, PollDeviceRequest>({
    mutationFn: (request) => devicesApi.pollDevice(deviceId, request),
    onSuccess: () => {
      // Обновляем данные устройства после опроса
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

// PON калькулятор - схемы
export function usePonSchemes(deviceId?: number) {
  return useQuery({
    queryKey: ['device', deviceId, 'pon', 'schemes'],
    queryFn: () => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.getPonSchemes(deviceId);
    },
    enabled: !!deviceId,
    staleTime: 30_000,
  });
}

export function usePonScheme(deviceId?: number, schemeId?: number) {
  return useQuery({
    queryKey: ['device', deviceId, 'pon', 'schemes', schemeId],
    queryFn: () => {
      if (!deviceId || !schemeId) {
        throw new Error('Device ID and Scheme ID are required');
      }
      return devicesApi.getPonScheme(deviceId, schemeId);
    },
    enabled: !!deviceId && !!schemeId,
  });
}

export function useSavePonScheme(deviceId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheme: { name: string; nodes: unknown[]; edges: unknown[] }) => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.savePonScheme(deviceId, scheme);
    },
    onSuccess: () => {
      if (deviceId) {
        queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'pon', 'schemes'] });
      }
    },
  });
}

export function useUpdatePonScheme(deviceId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ schemeId, scheme }: { schemeId: number; scheme: { name: string; nodes: unknown[]; edges: unknown[] } }) => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.updatePonScheme(deviceId, schemeId, scheme);
    },
    onSuccess: () => {
      if (deviceId) {
        queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'pon', 'schemes'] });
      }
    },
  });
}

export function useDeletePonScheme(deviceId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemeId: number) => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.deletePonScheme(deviceId, schemeId);
    },
    onSuccess: () => {
      if (deviceId) {
        queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'pon', 'schemes'] });
      }
    },
  });
}

// Хук для перезагрузки ONU
export function useRebootONU(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (onuId: string) => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.rebootONU(deviceId, onuId);
    },
    onSuccess: (response, onuId) => {
      // Добавляем задачу в store для отслеживания статуса
      if (response.task_id) {
        useDevicePonTasksStore.getState().add({
          id: response.task_id,
          deviceId,
          taskName: 'OnuReboot',
          status: 'scheduled',
          message: response.message,
          onus: [onuId],
          createdAt: new Date().toISOString(),
          source: 'local',
          origin: 'user',
          category: 'maintenance',
        });
      }
      // Инвалидируем список ONU после перезагрузки
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'onu'] });
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'pon', 'ports'] });
    },
  });
}

// Хук для установки профиля ONU
export function useSetONUProfile(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ onuId, profile }: { onuId: string; profile: string }) => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.setONUProfile(deviceId, onuId, profile);
    },
    onSuccess: () => {
      // Инвалидируем список ONU после изменения профиля
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'onu'] });
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'pon', 'ports'] });
    },
  });
}

// Хук для обновления заметок ONU
export function useUpdateONUNotes(deviceId: number, identifier: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notes: string) => {
      if (!deviceId || !identifier) {
        throw new Error('Device ID and identifier are required');
      }
      return devicesApi.updateONUNotes(deviceId, identifier, notes);
    },
    onSuccess: () => {
      // Инвалидируем детали ONU после обновления заметок
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'onu', identifier, 'details'] });
    },
  });
}

// Хук для получения метрик ONU с автообновлением (только уровни сигнала)
export function useONUMetrics(deviceId: number, identifier: string | null, enabled = true) {
  return useQuery({
    queryKey: ['device', deviceId, 'onu', identifier, 'metrics'],
    queryFn: () => {
      if (!identifier) {
        throw new Error('Identifier is required');
      }
      return devicesApi.getONUMetrics(deviceId, identifier);
    },
    enabled: enabled && !!deviceId && !!identifier,
    refetchInterval: 30000, // Обновляем каждые 30 секунд
    refetchIntervalInBackground: false, // Только когда вкладка активна
    staleTime: 10000, // Данные актуальны 10 секунд
  });
}

// Хук для получения списка профилей ONU с конкретного OLT
export function useDeviceONUProfiles(deviceId: number, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? !!deviceId;
  return useQuery({
    queryKey: ['device', deviceId, 'pon', 'profiles'],
    queryFn: () => devicesApi.getONUProfiles(deviceId),
    enabled,
    staleTime: 60_000, // Профили меняются редко, кешируем на 1 минуту
    refetchOnWindowFocus: false,
  });
}

// Hook для текущего адресного замера или честного DB fallback.
export function useONULiveMetrics(deviceId: number, identifier: string | null, enabled = true) {
  const isEnabled = enabled && !!deviceId && !!identifier;

  return useQuery({
    queryKey: ['device', deviceId, 'onu', identifier, 'live'],
    queryFn: () => {
      if (!identifier) {
        throw new Error('Identifier is required');
      }
      return devicesApi.getONULiveMetrics(deviceId, identifier);
    },
    enabled: isEnabled,
    // refetchInterval работает ТОЛЬКО когда запрос enabled
    // Это предотвращает выполнение запросов после размонтирования компонента
    refetchInterval: isEnabled ? 30000 : false, // Повторный запрос только когда enabled.
    refetchIntervalInBackground: false, // Только когда вкладка активна
    staleTime: 5000, // Клиентский кеш; фактический источник указан в ответе.
    gcTime: 60000, // Очистка из кеша через 1 минуту после размонтирования (предотвращает "призрачные" запросы)
  });
}

// PON карта - данные для отображения на карте
export function usePonMapData(deviceId?: number) {
  return useQuery({
    queryKey: ['device', deviceId, 'pon', 'map'],
    queryFn: () => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }
      return devicesApi.getPonMapData(deviceId);
    },
    enabled: !!deviceId,
    staleTime: 30_000,
  });
}

// Хук для запуска cable test
export function useStartCableTest(deviceId: number) {
  const queryClient = useQueryClient();

  return useMutation<StartCableTestResponse, unknown, StartCableTestRequest>({
    mutationFn: (request) => devicesApi.startCableTest(deviceId, request),
    onSuccess: () => {
      // Обновляем историю тестов
      queryClient.invalidateQueries({ queryKey: ['device', deviceId, 'cable-test'] });
    },
  });
}

// Хук для получения истории cable test
export function useCableTestHistory(deviceId: number, page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: ['device', deviceId, 'cable-test', 'history', page, limit],
    queryFn: () => devicesApi.getCableTestHistory(deviceId, page, limit),
    enabled: !!deviceId,
  });
}

// Хук для получения результатов cable test
export function useCableTestResults(deviceId: number, dateAdd: number | null, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? (!!deviceId && !!dateAdd);

  return useQuery({
    queryKey: ['device', deviceId, 'cable-test', dateAdd],
    queryFn: () => devicesApi.getCableTestResults(deviceId, dateAdd ?? 0),
    enabled,
  });
}

// Хук для лога активности устройства
export function useDeviceActivityLog(
  deviceId: number,
  filters: {
    page?: number;
    limit?: number;
    isDown?: boolean;
    dateFrom?: number;
    dateTo?: number;
  } = {},
  options: {
    enabled?: boolean;
  } = {},
) {
  const { page, limit, isDown, dateFrom, dateTo } = filters;
  const isEnabled = options.enabled ?? true;

  return useQuery({
    queryKey: [
      'device',
      deviceId,
      'activity-log',
      page ?? 1,
      limit ?? 50,
      isDown ?? null,
      dateFrom ?? null,
      dateTo ?? null,
    ],
    queryFn: () =>
      devicesApi.getActivityLog(deviceId, {
        page,
        limit,
        is_down: isDown,
        date_from: dateFrom,
        date_to: dateTo,
      }),
    enabled: !!deviceId && isEnabled,
    placeholderData: keepPreviousData,
  });
}

// Хук для списка ONU на устройстве

export function useDeviceONUList(
  deviceId: number,
  filter: ONUFilter & { page: number; limit: number; sort_by?: string; sort_desc?: boolean },
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? !!deviceId;
  const key = [
    'device',
    deviceId,
    'onu',
    filter.query ?? null,
    filter.olt_if_index ?? null,
    filter.is_online ?? null,
    filter.is_unknown ?? null,
    filter.is_level_bad ?? null,
    filter.page,
    filter.limit,
    filter.sort_by ?? null,
    filter.sort_desc ?? null,
  ] as const;

  return useQuery<ONUListResponse, Error, NormalizedOnuListResponse>({
    queryKey: key,
    queryFn: () => devicesApi.getONUList(deviceId, filter),
    select: normalizeOnuListResponse,
    placeholderData: keepPreviousData,
    enabled,
    // ✅ ОПТИМИЗАЦИЯ: Увеличиваем staleTime до 30 сек для синхронизации с backend OnuList polling интервалом
    // Backend: OnuList кэш TTL = 30 сек (из poller-go/internal/router/adaptive_cache.go)
    // Без этого таблица мерцает при действиях пользователя (фильтры, пагинация, скролл)
    // потому что staleTime истекает ДО полного интервала backend polling
    staleTime: 30_000, // 30 секунд - соответствует OnuList cache TTL на backend
    // ✅ ОПТИМИЗАЦИЯ: Не рефетчить при фокусе окна (данные обновляются через WebSocket)
    refetchOnWindowFocus: false,
    // ✅ ОПТИМИЗАЦИЯ: Не рефетчить при восстановлении соединения (будет WebSocket событие)
    refetchOnReconnect: false,
  });
}

// Хук для деталей конкретной ONU
export function useDeviceONUInfoByDevice(deviceId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['device', deviceId, 'onu-info'],
    queryFn: () => devicesApi.getONUInfoByDevice(deviceId),
    enabled: options?.enabled !== false && deviceId > 0,
    staleTime: 30000, // 30 секунд
    refetchInterval: 60000, // Обновлять каждую минуту для real-time
  });
}

export function useDeviceONUDetails(
  deviceId: number,
  identifier: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    refetchIntervalInBackground?: boolean;
    staleTime?: number;
  }
) {
  const enabled = options?.enabled ?? (!!deviceId && !!identifier);

  return useQuery({
    queryKey: ['device', deviceId, 'onu', 'details', identifier],
    queryFn: () => devicesApi.getONUDetails(deviceId, identifier ?? ''),
    enabled,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchIntervalInBackground,
    staleTime: options?.staleTime,
  });
}

export function useDeviceONULevelHistory(
  deviceId: number,
  identifier: string | null,
  params?: { date_from?: number; date_to?: number; limit?: number },
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? (!!deviceId && !!identifier);

  return useQuery({
    queryKey: ['device', deviceId, 'onu', 'level-history', identifier, params?.date_from, params?.date_to, params?.limit],
    queryFn: () => {
      return devicesApi.getONULevelHistory(deviceId, identifier ?? '', params);
    },
    enabled,
    staleTime: 30_000, // 30 секунд
    refetchOnWindowFocus: false,
  });
}

// SNMP Monitoring Metrics Types & Hooks

export interface SNMPMetricsKPI {
  success_rate: number;
  avg_response_time: number;
  timeout_rate: number;
  total_queries: number;
  total_timeouts: number;
  total_errors: number;
  active_sessions: number;
  circuit_breaker_hits: number;
}

export interface SNMPDeviceMetrics {
  device_id: number;
  vendor: string;
  success_rate: number;
  timeout_rate: number;
  avg_response_time: number;
  last_query_time: string;
  status: 'online' | 'offline' | 'throttled';
  circuit_breaker_state: 'closed' | 'open' | 'half-open';
}

export interface SNMPQueryLog {
  id: string;
  device_id: number;
  vendor: string;
  oid_level: number;
  duration_ms: number;
  success: boolean;
  error?: string;
  timestamp: string;
}

export interface SNMPPollingSchedule {
  device_id: number;
  vendor: string;
  interval_seconds: number;
  last_poll: string;
  next_poll: string;
  adaptive: boolean;
}

/**
 * Срез состояния SNMP-опроса.
 *
 * Считается из счётчиков в памяти сервиса, поэтому период — «с момента старта сервиса»
 * (поле scope). Истории здесь нет: графики по времени живут в Prometheus/Grafana, дублировать
 * их хранением в продукте нельзя — вторая система метрик легла бы на каждую инсталляцию.
 */
export interface SNMPStateVendor {
  vendor: string;
  total: number;
  success: number;
  failed: number;
  avg_duration_seconds: number;
}

export interface SNMPCircuitBreakerState {
  device_id: number;
  device_ip: string;
  state: 'closed' | 'open' | 'half-open';
}

export interface SNMPState {
  scope: string;
  total: number;
  success: number;
  failed: number;
  avg_duration_seconds: number;
  by_vendor: SNMPStateVendor[];
  circuit_breakers: SNMPCircuitBreakerState[];
}

async function fetchSNMPState(): Promise<SNMPState> {
  const response = await api.get('/monitoring/snmp/state');
  return response.data;
}

// Reset circuit breaker
async function resetCircuitBreaker(deviceId: number): Promise<void> {
  await api.post(`/monitoring/snmp/circuit-breaker/${deviceId}/reset`);
}

// Fetch SNMP history
async function fetchDeviceSNMPHistory(deviceId: number, limit: number = 50): Promise<AuditEntry[]> {
  const response = await api.get(`/devices/${deviceId}/snmp/history`, {
    params: { limit },
  });
  return response.data;
}

// React Query Hooks for SNMP Metrics

export function useSNMPState(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: ['snmp', 'state'],
    queryFn: fetchSNMPState,
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval ?? 10000, // 10 seconds
    staleTime: 5000,
  });
}

export function useDeviceSNMPHistory(deviceId: number, limit: number = 50) {
  return useQuery({
    queryKey: ['devices', deviceId, 'snmp-history', limit],
    queryFn: () => fetchDeviceSNMPHistory(deviceId, limit),
    enabled: !!deviceId,
  });
}

export function useResetCircuitBreakerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId: number) => resetCircuitBreaker(deviceId),
    onSuccess: (_, deviceId) => {
      queryClient.invalidateQueries({ queryKey: ['snmp', 'metrics', 'device', deviceId] });
    },
  });
}

// ✅ НОВОЕ (2026-01-22): Управление профилями вендоров

// ⚠️ ПУТИ: apiClient уже имеет baseURL '/api/v1'.
// Раньше все вызовы профилей передавали путь С префиксом → запрос уходил на
// /api/v1/api/v1/snmp/vendor-profiles и получал 404. То есть список профилей
// не работал НИКОГДА, независимо от заглушки репозитория. Префикс убран.
//
// ⚠️ Профили вендоров — витрина только для чтения: источник = конфигурационный файл
// администратора, запись через API отвечает 501. Мутации save/delete намеренно удалены,
// чтобы кнопка «Сохранить» не могла вернуться на экран.
export async function fetchVendorProfiles(): Promise<VendorProfileList> {
  const response = await api.get('/snmp/vendor-profiles');
  const data = response.data;
  // Конверт { items, source, read_only }. Старый формат (голый массив) поддерживаем,
  // чтобы фронт не падал на невыкаченном бэкенде.
  if (Array.isArray(data)) {
    return { items: data, source: 'config_file', read_only: true };
  }
  return {
    items: data?.items ?? [],
    source: data?.source ?? 'config_file',
    read_only: data?.read_only ?? true,
    // Диагностика источника: путь к файлу профилей и причина, если он не прочитан.
    // На невыкаченном бэкенде полей нет — витрина падает на статичную подсказку.
    source_path: data?.source_path,
    load_error: data?.load_error,
  };
}

export async function fetchVendorProfile(id: number): Promise<VendorProfile> {
  const response = await api.get(`/snmp/vendor-profiles/${id}`);
  return response.data;
}

export async function testVendorProfile(params: { profile: Partial<VendorProfile>, device_id: number }): Promise<VendorProfileTestResult> {
  const response = await api.post("/snmp/vendor-profiles/test", params);
  return response.data;
}

export function useTestVendorProfileMutation() {
  return useMutation({
    mutationFn: testVendorProfile,
  });
}

export async function fetchVendorProfileAuditLogs(id?: number, limit: number = 50): Promise<AuditEntry[]> {
  const response = await api.get('/snmp/vendor-profiles/audit-logs', {
    params: { resource_id: id, limit }
  });
  return toArray<AuditEntry>(response.data);
}

// Логи аудита профилей. `id` необязателен: без него читаем весь журнал по типу ресурса.
// ⚠️ Раньше страница ходила сюда голым fetch() без заголовка Authorization → всегда 401,
// ошибка глоталась в catch, таблица молча пустела. Теперь через общий api-клиент.
export function useVendorProfileAuditLogs(id?: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['snmp', 'vendor-profiles', id ?? 'all', 'audit-logs'],
    queryFn: () => fetchVendorProfileAuditLogs(id),
    enabled: options?.enabled !== false,
    staleTime: 30000,
  });
}

export function useVendorProfiles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['snmp', 'vendor-profiles'],
    queryFn: fetchVendorProfiles,
    enabled: options?.enabled !== false,
    staleTime: 60000,
  });
}

export function useVendorProfile(id?: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['snmp', 'vendor-profiles', id],
    queryFn: () => fetchVendorProfile(id!),
    enabled: (options?.enabled !== false) && !!id,
    staleTime: 60000,
  });
}

// useSaveVendorProfileMutation / useDeleteVendorProfileMutation удалены намеренно:
// запись профилей не поддерживается (бэкенд отвечает 501), а мутация в коде —
// приглашение вернуть на экран кнопку с ложным успехом.
